import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  RotateCcw,
  Save,
  X,
  PanelLeft,
  LayoutTemplate,
  ArrowDownRight,
  ArrowDownLeft,
  ArrowUpRight,
  ArrowUpLeft,
} from 'lucide-react';

import {
  toast,
} from '@workspace/ui';
import {
  MAP_THEME_CATALOG,
  THEME_CATALOG,
  UserSettingsAppearancePanel,
  UserSettingsPage,
  type UserSettingsChoiceOption,
  type UserSettingsPageAction,
  type UserSettingsPageNotice,
  type UserSettingsPageSection,
  type UserSettingsSwatchOption,
  type UserSettingsThemeOption,
  type UserSettingsTone,
} from '@workspace/silk-ui';
import type { ThemeEffect } from '@workspace/design-tokens';
import { THEME_EFFECT_METADATA, THEME_EFFECTS, THEME_EFFECT_PREVIEWS } from '@workspace/design-tokens';

import { usePreferences, type LayoutOption, type MapBasemapTheme, type ToastPosition } from '@/context/PreferencesContext';

const layoutOptions: UserSettingsChoiceOption[] = [
  {
    value: 'side-menu',
    label: 'Sidebar navigation',
    description: 'Primary navigation on the left with roomy content areas.',
    icon: <PanelLeft className="h-4 w-4" aria-hidden />,
  },
  {
    value: 'top-menu',
    label: 'Top navigation',
    description: 'Horizontal navigation bar with dropdown collections.',
    icon: <LayoutTemplate className="h-4 w-4" aria-hidden />,
  }
];

const toastPositionOptions: UserSettingsChoiceOption[] = [
  {
    value: 'bottom-right',
    label: 'Bottom Right',
    description: 'Default placement for action feedback.',
    icon: <ArrowDownRight className="h-4 w-4" aria-hidden />,
  },
  {
    value: 'bottom-left',
    label: 'Bottom Left',
    description: 'Keeps toasts near left-side navigation.',
    icon: <ArrowDownLeft className="h-4 w-4" aria-hidden />,
  },
  {
    value: 'top-right',
    label: 'Top Right',
    description: 'Traditional desktop notification corner.',
    icon: <ArrowUpRight className="h-4 w-4" aria-hidden />,
  },
  {
    value: 'top-left',
    label: 'Top Left',
    description: 'Useful when right-side overlays are common.',
    icon: <ArrowUpLeft className="h-4 w-4" aria-hidden />,
  },
];

const themeEffectOptions: UserSettingsSwatchOption[] = THEME_EFFECTS.map(effect => ({
  value: effect,
  label: THEME_EFFECT_METADATA[effect].label,
  description: THEME_EFFECT_METADATA[effect].description,
  swatchStyle: THEME_EFFECT_PREVIEWS[effect]
}));

const themeOptions: UserSettingsThemeOption[] = THEME_CATALOG.map(theme => ({
  value: theme.value,
  label: theme.label,
  description: theme.description,
  preview: theme.preview,
  accent: theme.accent
}));

const basemapThemeOptions: UserSettingsThemeOption[] = MAP_THEME_CATALOG.map(theme => ({
  value: theme.value as MapBasemapTheme,
  label: theme.label,
  description: theme.description,
  preview: theme.preview,
  accent: theme.accent
}));

export default function SettingsPage() {
  const {
    theme,
    savedTheme,
    themeEffect,
    savedThemeEffect,
    isThemePreview,
    previewTheme,
    cancelThemePreview,
    setThemeEffect,
    layout,
    savedLayout,
    setLayout,
    toastPosition,
    savedToastPosition,
    setToastPosition,
    basemapTheme,
    basemapThemeOverrideEnabled,
    savedBasemapThemeOverrideEnabled,
    basemapThemeOverrideSelection,
    savedBasemapThemeOverrideSelection,
    setBasemapThemeOverrideEnabled,
    setBasemapTheme,
    savePreferences,
    resetPreferences,
    loading,
    error
  } = usePreferences();

  const [selectedTheme, setSelectedTheme] = useState(theme);
  const [selectedEffect, setSelectedEffect] = useState(themeEffect);
  const [selectedLayout, setSelectedLayout] = useState(layout);
  const [selectedToastPosition, setSelectedToastPosition] = useState(toastPosition);
  const [selectedBasemapTheme, setSelectedBasemapTheme] =
    useState<MapBasemapTheme>(basemapThemeOverrideSelection);
  const [selectedBasemapThemeOverrideEnabled, setSelectedBasemapThemeOverrideEnabled] =
    useState(basemapThemeOverrideEnabled);
  const [saving, setSaving] = useState(false);
  const [savingAction, setSavingAction] = useState<'save' | 'reset' | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [activeSectionId, setActiveSectionId] = useState('appearance');
  const [confirmingReset, setConfirmingReset] = useState(false);

  useEffect(() => {
    setSelectedTheme(theme);
  }, [theme]);

  useEffect(() => {
    setSelectedEffect(themeEffect);
  }, [themeEffect]);

  useEffect(() => {
    setSelectedLayout(layout);
  }, [layout]);

  useEffect(() => {
    setSelectedToastPosition(toastPosition);
  }, [toastPosition]);

  useEffect(() => {
    setSelectedBasemapTheme(basemapThemeOverrideSelection);
  }, [basemapThemeOverrideSelection]);

  useEffect(() => {
    setSelectedBasemapThemeOverrideEnabled(basemapThemeOverrideEnabled);
  }, [basemapThemeOverrideEnabled]);

  // Clear the reset confirmation if user navigates away or loading kicks in
  useEffect(() => {
    if (loading || saving) setConfirmingReset(false);
  }, [loading, saving]);

  const hasUnsavedChanges = useMemo(
    () =>
      selectedTheme !== savedTheme ||
      selectedEffect !== savedThemeEffect ||
      selectedLayout !== savedLayout ||
      selectedToastPosition !== savedToastPosition ||
      selectedBasemapTheme !== savedBasemapThemeOverrideSelection ||
      selectedBasemapThemeOverrideEnabled !== savedBasemapThemeOverrideEnabled,
    [
      savedBasemapThemeOverrideEnabled,
      savedBasemapThemeOverrideSelection,
      savedLayout,
      savedTheme,
      savedThemeEffect,
      savedToastPosition,
      selectedBasemapTheme,
      selectedBasemapThemeOverrideEnabled,
      selectedEffect,
      selectedLayout,
      selectedTheme,
      selectedToastPosition
    ]
  );

  const disableInteractions = saving || loading;
  const activeError = actionError || error;

  const handleThemeSelect = useCallback(
    (next: typeof theme) => {
      setSelectedTheme(next);
      previewTheme(next);
    },
    [previewTheme, theme]
  );

  const handleEffectSelect = useCallback(
    (next: ThemeEffect) => {
      setSelectedEffect(next);
      setThemeEffect(next);
    },
    [setThemeEffect]
  );

  const handleLayoutSelect = useCallback(
    (next: LayoutOption) => {
      setSelectedLayout(next);
      setLayout(next);
    },
    [setLayout]
  );

  const handleToastPositionSelect = useCallback(
    (next: ToastPosition) => {
      setSelectedToastPosition(next);
      setToastPosition(next);
    },
    [setToastPosition]
  );

  const handleBasemapThemeSelect = useCallback(
    (next: MapBasemapTheme) => {
      setSelectedBasemapTheme(next);
      setBasemapTheme(next);
    },
    [setBasemapTheme]
  );

  const handleBasemapThemeOverrideToggle = useCallback(
    (next: boolean) => {
      setSelectedBasemapThemeOverrideEnabled(next);
      setBasemapThemeOverrideEnabled(next);
    },
    [setBasemapThemeOverrideEnabled]
  );

  const handleCancel = useCallback(() => {
    setSelectedTheme(savedTheme);
    setSelectedEffect(savedThemeEffect);
    setSelectedLayout(savedLayout);
    setSelectedToastPosition(savedToastPosition);
    setSelectedBasemapTheme(savedBasemapThemeOverrideSelection);
    setSelectedBasemapThemeOverrideEnabled(savedBasemapThemeOverrideEnabled);
    cancelThemePreview();
    setThemeEffect(savedThemeEffect);
    setLayout(savedLayout);
    setToastPosition(savedToastPosition);
    setBasemapTheme(savedBasemapThemeOverrideSelection);
    setBasemapThemeOverrideEnabled(savedBasemapThemeOverrideEnabled);
    setActionError(null);
    setConfirmingReset(false);
  }, [
    cancelThemePreview,
    savedBasemapThemeOverrideEnabled,
    savedBasemapThemeOverrideSelection,
    savedTheme,
    savedThemeEffect,
    savedLayout,
    savedToastPosition,
    setBasemapTheme,
    setBasemapThemeOverrideEnabled,
    setThemeEffect,
    setLayout,
    setToastPosition
  ]);

  const handleSave = useCallback(async () => {
    setSaving(true);
    setSavingAction('save');
    setActionError(null);
    setConfirmingReset(false);
    try {
      const updates: {
        theme?: typeof theme;
        themeEffect?: ThemeEffect;
        layout?: LayoutOption;
        toastPosition?: ToastPosition;
        basemapTheme?: MapBasemapTheme;
        basemapThemeOverrideEnabled?: boolean;
      } = {};

      if (selectedTheme !== savedTheme) {
        updates.theme = selectedTheme;
      }
      if (selectedEffect !== savedThemeEffect) {
        updates.themeEffect = selectedEffect;
      }
      if (selectedLayout !== savedLayout) {
        updates.layout = selectedLayout;
      }
      if (selectedToastPosition !== savedToastPosition) {
        updates.toastPosition = selectedToastPosition;
      }
      if (selectedBasemapTheme !== savedBasemapThemeOverrideSelection) {
        updates.basemapTheme = selectedBasemapTheme;
      }
      if (selectedBasemapThemeOverrideEnabled !== savedBasemapThemeOverrideEnabled) {
        updates.basemapThemeOverrideEnabled = selectedBasemapThemeOverrideEnabled;
      }

      await savePreferences(updates);
      toast.success('Preferences saved.');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save preferences';
      setActionError(message);
      toast.error(message);
    } finally {
      setSaving(false);
      setSavingAction(null);
    }
  }, [
    savePreferences,
    savedBasemapThemeOverrideEnabled,
    savedBasemapThemeOverrideSelection,
    savedLayout,
    savedTheme,
    savedThemeEffect,
    savedToastPosition,
    selectedBasemapTheme,
    selectedBasemapThemeOverrideEnabled,
    selectedEffect,
    selectedLayout,
    selectedTheme,
    selectedToastPosition,
    theme
  ]);

  const handleReset = useCallback(async () => {
    setSaving(true);
    setSavingAction('reset');
    setActionError(null);
    setConfirmingReset(false);
    try {
      await resetPreferences();
      toast.success('Preferences reset to defaults.');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to reset preferences';
      setActionError(message);
      toast.error(message);
    } finally {
      setSaving(false);
      setSavingAction(null);
    }
  }, [resetPreferences]);

  const primaryLabel = savingAction === 'save' ? 'Saving…' : 'Save changes';
  const resetLabel = savingAction === 'reset' ? 'Resetting…' : confirmingReset ? 'Confirm reset?' : 'Reset';

  const pageActions = useMemo<UserSettingsPageAction[]>(() => {
    const actions: UserSettingsPageAction[] = [
      {
        id: 'reset',
        label: resetLabel,
        icon: <RotateCcw className="h-3.5 w-3.5" aria-hidden />,
        disabled: disableInteractions,
        tone: confirmingReset ? 'danger' : 'default',
      }
    ];

    if (confirmingReset) {
      // Offer an escape hatch when awaiting reset confirmation
      actions.push({
        id: 'cancel-reset',
        label: 'Cancel',
        icon: <X className="h-3.5 w-3.5" aria-hidden />,
        disabled: disableInteractions,
      });
    } else if (hasUnsavedChanges) {
      actions.push({
        id: 'cancel',
        label: 'Cancel',
        icon: <X className="h-3.5 w-3.5" aria-hidden />,
        disabled: disableInteractions,
      });
    }

    actions.push({
      id: 'save',
      label: primaryLabel,
      icon: <Save className="h-3.5 w-3.5" aria-hidden />,
      disabled: disableInteractions || !hasUnsavedChanges,
      tone: 'primary'
    });

    return actions;
  }, [confirmingReset, disableInteractions, hasUnsavedChanges, primaryLabel, resetLabel]);

  const handlePageAction = useCallback((actionId: string) => {
    if (actionId === 'reset') {
      // First click arms the confirmation; second click fires the reset
      if (!confirmingReset) {
        setConfirmingReset(true);
        return;
      }
      void handleReset();
      return;
    }
    if (actionId === 'cancel-reset') {
      setConfirmingReset(false);
      return;
    }
    if (actionId === 'cancel') {
      handleCancel();
      return;
    }
    if (actionId === 'save') {
      void handleSave();
    }
  }, [confirmingReset, handleCancel, handleReset, handleSave]);

  const mapThemeDisplayValue = selectedBasemapThemeOverrideEnabled ? selectedBasemapTheme : basemapTheme;
  const mapThemeDescription = selectedBasemapThemeOverrideEnabled
    ? basemapThemeOptions.find(option => option.value === selectedBasemapTheme)?.description
    : `Map theme currently follows ${themeOptions.find(option => option.value === mapThemeDisplayValue)?.label ?? mapThemeDisplayValue}.`;

  // Preview notice always renders first so it doesn't look like a broken/error state
  const notices = useMemo<UserSettingsPageNotice[]>(() => {
    const next: UserSettingsPageNotice[] = [];
    if (isThemePreview) {
      next.push({
        id: 'preview',
        title: 'Theme preview active',
        description: 'Theme changes preview immediately and save with your preferences when you commit them.',
        tone: 'info'
      });
    }
    if (loading) {
      next.push({
        id: 'loading',
        title: 'Loading preferences',
        description: 'Loading your saved appearance preferences.',
        tone: 'neutral'
      });
    }
    if (activeError) {
      next.push({
        id: 'error',
        title: 'Settings unavailable',
        description: activeError,
        tone: 'danger'
      });
    }
    return next;
  }, [activeError, isThemePreview, loading]);

  const sections = useMemo<UserSettingsPageSection[]>(() => {
    return [
      {
        id: 'appearance',
        label: 'Appearance',
        title: 'Workspace appearance',
        eyebrow: 'Personalization',
        description: 'Customize the visual interface of your workspace and maps.',
        statusLabel: isThemePreview ? 'Previewing' : 'Live',
        statusTone: (isThemePreview ? 'info' : 'neutral') as UserSettingsTone,
        content: (
          <UserSettingsAppearancePanel
            selectedTheme={selectedTheme}
            activeTheme={theme}
            themeOptions={themeOptions}
            onThemeChange={value => handleThemeSelect(value as typeof theme)}
            selectedMapTheme={mapThemeDisplayValue}
            mapThemeOptions={basemapThemeOptions}
            onMapThemeChange={value => handleBasemapThemeSelect(value as MapBasemapTheme)}
            mapThemeOverrideEnabled={selectedBasemapThemeOverrideEnabled}
            onMapThemeOverrideEnabledChange={handleBasemapThemeOverrideToggle}
            mapThemeDescription={mapThemeDescription}
            selectedToastPosition={selectedToastPosition}
            toastPositionOptions={toastPositionOptions}
            onToastPositionChange={value => handleToastPositionSelect(value as ToastPosition)}
            selectedEffect={selectedEffect}
            effectOptions={themeEffectOptions}
            onEffectChange={value => handleEffectSelect(value as ThemeEffect)}
            selectedLayout={selectedLayout}
            layoutOptions={layoutOptions}
            onLayoutChange={value => handleLayoutSelect(value as LayoutOption)}
            disabled={disableInteractions}
          />
        )
      }
    ];
  }, [
    disableInteractions,
    handleBasemapThemeSelect,
    handleBasemapThemeOverrideToggle,
    handleEffectSelect,
    handleLayoutSelect,
    handleThemeSelect,
    handleToastPositionSelect,
    isThemePreview,
    mapThemeDescription,
    mapThemeDisplayValue,
    selectedBasemapThemeOverrideEnabled,
    selectedEffect,
    selectedLayout,
    selectedTheme,
    selectedToastPosition,
    theme
  ]);

  useEffect(() => {
    if (!sections.some(section => section.id === activeSectionId)) {
      setActiveSectionId(sections[0]?.id ?? 'appearance');
    }
  }, [activeSectionId, sections]);

  return (
    <UserSettingsPage
      title="Personalisation"
      description="Choose the appearance that works best for your account workspace."
      eyebrow="User settings"
      actions={pageActions}
      notices={notices}
      sections={sections}
      activeSectionId={activeSectionId}
      onSectionSelect={setActiveSectionId}
      onAction={handlePageAction}
    />
  );
}
